import { useState } from 'react';
import {
  Card, CardHeader, CardContent, Grid, Stack, TextField, MenuItem, Autocomplete, Button, IconButton,
  List, ListItem, ListItemText, Divider, Typography, Alert, Snackbar, Chip,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { clientsApi } from '../../api/clients.api';
import { clientMappingApi, type AvailableProduct, type AvailableDealer } from '../../api/clientMapping.api';
import { extractApiError } from '../../api/error';

/** PDI admin screen: maintain Client → Products and Client → Dealers visibility mappings. */
export function ClientMappingsPage() {
  const qc = useQueryClient();
  const [clientId, setClientId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [addProduct, setAddProduct] = useState<AvailableProduct | null>(null);
  const [addDealer, setAddDealer] = useState<AvailableDealer | null>(null);

  const { data: clients } = useQuery({ queryKey: ['clients-lookup'], queryFn: () => clientsApi.list({ limit: 500 }) });
  const enabled = clientId != null;
  const products = useQuery({ queryKey: ['cmap-products', clientId], queryFn: () => clientMappingApi.products(clientId!), enabled });
  const availProducts = useQuery({ queryKey: ['cmap-products-avail', clientId], queryFn: () => clientMappingApi.availableProducts(clientId!), enabled });
  const dealers = useQuery({ queryKey: ['cmap-dealers', clientId], queryFn: () => clientMappingApi.dealers(clientId!), enabled });
  const availDealers = useQuery({ queryKey: ['cmap-dealers-avail', clientId], queryFn: () => clientMappingApi.availableDealers(clientId!), enabled });

  const refreshProducts = () => { qc.invalidateQueries({ queryKey: ['cmap-products', clientId] }); qc.invalidateQueries({ queryKey: ['cmap-products-avail', clientId] }); };
  const refreshDealers = () => { qc.invalidateQueries({ queryKey: ['cmap-dealers', clientId] }); qc.invalidateQueries({ queryKey: ['cmap-dealers-avail', clientId] }); };

  const mapProduct = useMutation({ mutationFn: () => clientMappingApi.mapProduct(clientId!, addProduct!.product_id), onSuccess: () => { setAddProduct(null); setToast('Product mapped'); refreshProducts(); }, onError: (e) => setToast(extractApiError(e)) });
  const unmapProduct = useMutation({ mutationFn: (id: number) => clientMappingApi.unmapProduct(id), onSuccess: () => { setToast('Product removed'); refreshProducts(); }, onError: (e) => setToast(extractApiError(e)) });
  const mapDealer = useMutation({ mutationFn: () => clientMappingApi.mapDealer(clientId!, addDealer!.dealer_id), onSuccess: () => { setAddDealer(null); setToast('Dealer mapped'); refreshDealers(); }, onError: (e) => setToast(extractApiError(e)) });
  const unmapDealer = useMutation({ mutationFn: (id: number) => clientMappingApi.unmapDealer(id), onSuccess: () => { setToast('Dealer removed'); refreshDealers(); }, onError: (e) => setToast(extractApiError(e)) });

  return (
    <>
      <PageHeader title="Client Mappings" subtitle="Control which Products and Dealers each Client can access (Protection Device Installation)." />

      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Client" value={clientId ?? ''} onChange={(e) => setClientId(Number(e.target.value))} sx={{ minWidth: 320 }}>
          {(clients?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName} ({c.clientCode})</MenuItem>)}
        </TextField>
      </Stack>

      {!enabled ? <EmptyState title="Select a client" description="Pick a client to manage its mapped products and dealers." /> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Mapped Products" titleTypographyProps={{ variant: 'subtitle1' }}
                subheader="Only these products will be orderable for this client." />
              <CardContent>
                <Stack direction="row" spacing={1} mb={1}>
                  <Autocomplete sx={{ flex: 1 }} options={availProducts.data ?? []} getOptionLabel={(o) => `${o.name} (${o.product_code})`}
                    value={addProduct} onChange={(_e, v) => setAddProduct(v)} isOptionEqualToValue={(o, v) => o.product_id === v.product_id}
                    renderInput={(p) => <TextField {...p} size="small" label="Add product…" />} />
                  <Button startIcon={<Add />} variant="contained" disabled={!addProduct || mapProduct.isPending} onClick={() => mapProduct.mutate()}>Add</Button>
                </Stack>
                <Divider />
                {(products.data?.length ?? 0) === 0 ? <EmptyState title="No products mapped" dense /> : (
                  <List dense>
                    {products.data!.map((p) => (
                      <ListItem key={p.mapping_id} secondaryAction={<IconButton edge="end" size="small" color="error" onClick={() => unmapProduct.mutate(p.mapping_id)}><Delete fontSize="small" /></IconButton>}>
                        <ListItemText primary={`${p.name} (${p.product_code})`} secondary={`₹${Number(p.unit_price ?? 0).toLocaleString('en-IN')} · ${p.category ?? '—'}`} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Mapped Dealers" titleTypographyProps={{ variant: 'subtitle1' }}
                subheader="Dealers that serve this client." />
              <CardContent>
                <Stack direction="row" spacing={1} mb={1}>
                  <Autocomplete sx={{ flex: 1 }} options={availDealers.data ?? []} getOptionLabel={(o) => `${o.dealer_name} (${o.dealer_code})`}
                    value={addDealer} onChange={(_e, v) => setAddDealer(v)} isOptionEqualToValue={(o, v) => o.dealer_id === v.dealer_id}
                    renderInput={(p) => <TextField {...p} size="small" label="Add dealer…" />} />
                  <Button startIcon={<Add />} variant="contained" disabled={!addDealer || mapDealer.isPending} onClick={() => mapDealer.mutate()}>Add</Button>
                </Stack>
                <Divider />
                {(dealers.data?.length ?? 0) === 0 ? <EmptyState title="No dealers mapped" dense /> : (
                  <List dense>
                    {dealers.data!.map((d) => (
                      <ListItem key={d.mapping_id} secondaryAction={<IconButton edge="end" size="small" color="error" onClick={() => unmapDealer.mutate(d.mapping_id)}><Delete fontSize="small" /></IconButton>}>
                        <ListItemText primary={`${d.dealer_name} (${d.dealer_code})`} secondary={<Chip size="small" label={d.status} />} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}><Typography variant="caption" color="text.secondary">Phase 1 establishes these mappings. Product-visibility enforcement at order placement and the client portal follow in later phases.</Typography></Grid>
        </Grid>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
